import type { PropsWithoutRef, DetailedHTMLProps, SelectHTMLAttributes } from 'react';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';

import { Label, LoadingIndicator } from '../../atoms';

const ComponentContainer = styled.div`
  position: relative;
`;

function getChevronUrl(color: string) {
  // TODO: We have to give this as bg - hence why not using Icon component.
  // Let's check this further down the line how to optimise it.
  return (
    "\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='" +
    encodeURIComponent(color) +
    "' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\""
  );
}

const StyledSelect = styled.select<{ isLoading?: boolean; hasError?: boolean }>`
  ${({ theme, isLoading, hasError }) => css`
    height: 50px; // TODO: Make this the same as input fields, between browsers!
    box-sizing: border-box;
    outline: none;
    box-shadow: none;
    border-radius: ${theme.radius};
    width: 100%;
    color: ${isLoading ? 'transparent' : theme.palette.text.onSurface.subtle};
    border: 1px solid ${hasError ? theme.palette.accent.secondary.strong : theme.palette.text.onSurface.subtle};
    padding-left: ${theme.spacing(3)};
    padding-right: ${theme.spacing(10)};
    background-image: url(${getChevronUrl(
      hasError ? theme.palette.accent.secondary.strong : theme.palette.text.onSurface.subtle
    )});
    background-position: right 0.5rem center;
    background-repeat: no-repeat;
    background-size: 1.5em 1.5em;
    appearance: none;

    font-family: ShellBook;
    font-size: 16px;
    line-height: 24px;

    &:not([disabled]) {
      background-color: ${theme.palette.background.surface};
      &:hover {
        border-color: ${theme.palette.focus.strong};
        background-color: ${theme.palette.focus.subtle};
      }
      &:focus {
        border-color: ${theme.palette.focus.strong};
      }
    }

    &[disabled] {
      cursor: not-allowed;
      border-color: ${theme.palette.text.onSurface.subtle};
      background-color: ${theme.palette.text.onSurface.inactive};
    }
  `}
`;

const LoadingHolder = styled.div`
  position: absolute;
  left: 0;
  top: 32px;
  right: 0;
  bottom: 0;
  padding-left: 8px;
  padding-top: 8px;
`;

export const ErrorText = styled.div`
  color: ${({ theme }) => theme.palette.accent.secondary.strong};
  margintop: ${({ theme }) => theme.spacing(1)};
  ${({ theme }) => theme.typography.paragraph.small.normal};
`;

interface SelectItem {
  value: string | number;
  label: string;
}

export type SelectProps = PropsWithoutRef<
  DetailedHTMLProps<SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement>
> & {
  label?: string;
  className?: string;
  isLoading?: boolean;
  items: SelectItem[];
  placeholderItem?: SelectItem;
  error?: {
    message?: string;
  };
};

function keyExtractor(value: string | number, label: string): string {
  return `${label}-${value}`;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, id, name, className, isLoading, children, items, placeholderItem, ...selectProps }, ref) => {
    const errorId = `_input_${id ?? name}_error_message`;

    return (
      <ComponentContainer className={className}>
        {label && <Label htmlFor={name}>{label}</Label>}
        <StyledSelect
          hasError={!!error?.message}
          isLoading={isLoading}
          aria-label={label}
          name={name}
          {...selectProps}
          ref={ref}
        >
          {placeholderItem && (
            <option
              key={keyExtractor(placeholderItem.value, placeholderItem.label)}
              value={placeholderItem.value}
              disabled
              hidden
            >
              {placeholderItem.label}
            </option>
          )}
          {items &&
            Array.isArray(items) &&
            items.map(({ value, label: itemLabel }) => (
              <option key={keyExtractor(value, itemLabel)} value={value}>
                {itemLabel}
              </option>
            ))}
          {children}
        </StyledSelect>
        {isLoading && (
          <LoadingHolder>
            <LoadingIndicator animate />
          </LoadingHolder>
        )}
        {error?.message && (
          <ErrorText role="alert" id={errorId}>
            {error.message}
          </ErrorText>
        )}
      </ComponentContainer>
    );
  }
);

Select.displayName = 'Select';
